import { useCallback, useEffect, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import isString from 'lodash/isString';
import uniqBy from 'lodash/uniqBy';
import { useTranslation } from 'react-i18next';

import {
  getPlanReportOptions,
  UserPlanStatus,
} from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';
import { useFlags } from '@/hooks/useFlags';
import useGetXPSummaryReportByUser from '@/hooks/useGetXPSummaryReportByUser';
import { useLicenseeStaffUsesReportsPicker } from '@/hooks/useLeaderOrLicenseeLocations';
import { usePlanLocations } from '@/hooks/usePlanLocations';

import { arrayToCommaString } from '@/utils/arrayToCommaString';
import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { convertDateForReports } from '@/utils/date';
import { itemByLanguage } from '@/utils/language';
import { getTimeString } from '@/utils/time';
import { userHasRegulatedLocation } from '@/utils/training-plans/regulated-states';

import Constants from '@/constants';

import { processUserName } from '@/services/modelHelper';

import { PrintReportSubject } from './state';
import {
  buildComplianceTableBody,
  buildPrintReportFootnotes,
  buildStoreTableBody,
  emptyPrintReportFootnotesResult,
  type PrintReportRow,
  splitPrintReportRows,
} from './utils';

import { activeLicenseeLocationAtom } from '@/atoms/licensee-location';

interface UsePrintReportDataParams {
  subject: PrintReportSubject | null;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getFoodSafetyImageFromPlanReport = (
  planReport: unknown,
  selectedLocations: string[],
): string | undefined => {
  if (!isRecord(planReport)) return undefined;

  const foodSafetyLettersRaw = planReport.foodSafetyLetters;
  if (!Array.isArray(foodSafetyLettersRaw)) return undefined;

  const letters = foodSafetyLettersRaw
    .filter(isRecord)
    .map(letter => {
      const maybeNestedFoodSafetyLetter = letter.foodSafetyLetter;
      const nestedFoodSafetyLetter = isRecord(maybeNestedFoodSafetyLetter)
        ? maybeNestedFoodSafetyLetter
        : undefined;

      const nestedContent = nestedFoodSafetyLetter?.content;
      const directContent = letter.content;
      const letterLocations = letter.locations;

      return {
        content: isString(nestedContent)
          ? nestedContent
          : isString(directContent)
            ? directContent
            : undefined,
        locations: Array.isArray(letterLocations)
          ? letterLocations.filter((entry): entry is string => isString(entry))
          : [],
      };
    })
    .filter(
      (letter): letter is { content: string; locations: string[] } =>
        !!letter.content,
    );

  if (!letters.length) return undefined;

  const selectedLetter = letters.find(({ locations }) =>
    locations.some(location => selectedLocations.includes(location)),
  );

  return selectedLetter?.content ?? letters[0]?.content;
};

/**
 * Unified hook to fetch, map, and generate print report data.
 * Handles both plan reports (compliance and non-compliance) and team member reports.
 */
export const usePrintReportData = ({ subject }: UsePrintReportDataParams) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const { planDescriptions: planDescriptionsFF } = useFlags();

  const isLicenseeUser = auth?.isLicensee ?? false;
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;
  const activeLicenseeLocation = useAtomValue(activeLicenseeLocationAtom);
  const licenseeStaffUsesReportsPicker = useLicenseeStaffUsesReportsPicker();

  const isPlanPrint = subject?.type === 'plan';
  const isTeamMemberPrint = subject?.type === 'teamMember';
  const isCompliance = isPlanPrint && subject.plan.type === 'COMPLIANCE';

  // Lazy fetch state - queries stay disabled until startFetching is called
  const [shouldFetch, setShouldFetch] = useState(false);
  const [_selectedLocations, setSelectedLocations] = useState<string[] | null>(
    null,
  );

  const subjectId = isPlanPrint ? subject.plan.id : subject?.id;

  // Reset fetch state when subject changes
  useEffect(() => {
    setShouldFetch(false);
  }, [subjectId]);

  const startFetching = useCallback((locations: string[]) => {
    setSelectedLocations(locations);
    setShouldFetch(true);
  }, []);

  // Plan locations callback - compute on demand
  const { getLocationsByPlan } = usePlanLocations();
  const printLocations = isPlanPrint ? getLocationsByPlan(subject.plan) : [];

  // --- Plan Report Data Fetching ---
  const useLicenseePickerLocation =
    (isLicenseeUser && !userIsLicenseeStaff) || licenseeStaffUsesReportsPicker;
  // Plan-derived locations (incl. SC → 00000) take precedence over the Reports
  // picker so fetch locationNumbers match modal options from getLocationsByPlan.
  const licenseePickerLocation =
    useLicenseePickerLocation && activeLicenseeLocation?.number
      ? activeLicenseeLocation.number
      : undefined;

  const {
    data: planReportAndAssignments,
    isFetching: isPlanReportFetching,
    error: planReportError,
  } = useQuery({
    ...getPlanReportOptions({
      path: { planId: isPlanPrint ? subject.plan.id : 'no-id' },
      query: {
        locationNumbers: printLocations.length
          ? printLocations
          : licenseePickerLocation
            ? [licenseePickerLocation]
            : undefined,
      },
    }),
    enabled: shouldFetch && isPlanPrint,
  });

  // --- Team Member Report Data Fetching ---
  const teamMemberId = isTeamMemberPrint ? subject.id : '';
  const shouldUseOldFetch = !!teamMemberId;

  const {
    xpReportsData: teamMemberXpData,
    isFetchingXpReport: isTeamMemberXpFetching,
    errorXpReports: teamMemberXpError,
  } = useGetXPSummaryReportByUser(
    teamMemberId,
    !shouldFetch || !shouldUseOldFetch,
  );

  // Non-compliance plan report data - filter and map at report generation time
  const planReportData = planReportAndAssignments?.planUserReports;

  // Build non-compliance report data, filtering by selected locations
  const buildNonComplianceReport = (
    locations: string[],
  ): Record<string, string | undefined>[] => {
    if (!planReportData) return [];

    const locationCount = printLocations.length;

    return uniqBy(
      planReportData.filter(entry =>
        locationCount > 1 && entry.user?.locations.length
          ? entry.user.locations.some(location => locations.includes(location))
          : entry,
      ),
      entry => entry.user?.id,
    )
      .sort((a, b) =>
        processUserName(a.user).localeCompare(processUserName(b.user)),
      )
      .map(entry => ({
        [Constants.REPORT_TABLE.NAME]: processUserName(entry.user),
        [Constants.REPORT_TABLE.DATE_OF_BIRTH]:
          entry.user?.birthDate ?? t('Generic.na'),
        [Constants.REPORT_TABLE.ITEMS_COMPLETED]: `${
          entry.stepsComplete ?? 0
        }/${planReportAndAssignments.totalSteps}`,
        [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: getTimeString(
          Math.ceil((entry.timeTakenInSeconds ?? 0) / 60),
        ),
        [Constants.REPORT_TABLE.STATUS]: entry.status
          ? t(
              `TrainingPlans.statusOptions.${
                Constants.TRAINING_PLANS_STATUSES[entry.status]
              }`,
            )
          : t('Generic.na'),
        [Constants.REPORT_TABLE.COMPLETION_DATE]: (() => {
          const date =
            entry.latestCompletionReport?.completionDate ??
            entry.completionDate;
          return date ? convertDateForReports(date) : t('Generic.na');
        })(),
        id: planReportAndAssignments.id,
        [Constants.REPORT_TABLE.EXPIRATION_DATE]: (() => {
          const date =
            entry.latestCompletionReport?.expirationDate ??
            entry.certificateExpirationDate;
          return date ? convertDateForReports(date) : t('Generic.na');
        })(),
      }));
  };

  // Team member plans data combined
  const { xpPlans } = (() => {
    const plans: UserPlanStatus[] = (teamMemberXpData?.planStatus ?? []).filter(
      plan => plan.planType !== 'COMPLIANCE',
    );

    return {
      xpPlans: plans,
    };
  })();

  // --- Team Member Mapping Helpers ---

  const getTeamMemberTimeString = (time: number) => {
    const timeSpentHrs = Math.floor(time / 60);
    const timeSpentMins = time % 60;
    return `${
      timeSpentHrs > 0 ? `${timeSpentHrs} ${t('Generic.hour')}` : ''
    } ${timeSpentMins} ${t('Generic.mins')}`;
  };

  const mapXpStorePlanToReport = (plan: UserPlanStatus): PrintReportRow => {
    const planName = itemByLanguage(plan.planName) ?? '';
    const planTimeSpent = getTeamMemberTimeString(
      Math.ceil(plan.timeTakenInSeconds / 60),
    );
    const planDescription = itemByLanguage(plan.description) ?? '';

    return {
      planId: plan.planId,
      planName,
      planType: plan.planType,
      ...(planDescription && { planDescription }),
      [Constants.REPORT_TABLE.PLAN]: planName,
      [Constants.REPORT_TABLE.ITEMS_COMPLETED]:
        `${plan.completedSteps}/${plan.totalSteps}`,
      [Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN]: planTimeSpent,
      [Constants.REPORT_TABLE.STATUS]: t(
        `TrainingPlans.statusOptions.${
          Constants.TRAINING_PLANS_STATUSES[plan.planStatus]
        }`,
      ),
      [Constants.REPORT_TABLE.COMPLETION_DATE]: plan.completionDate
        ? convertDateForReports(plan.completionDate)
        : t('Generic.na'),
      [Constants.REPORT_TABLE.EXPIRATION_DATE]: t('Generic.na'),
    };
  };

  const shouldIncludePlanDescriptions =
    planDescriptionsFF && userHasRegulatedLocation(auth?.locationsWithRoles);

  // --- Loading & Error State ---
  const isFetching = isPlanPrint
    ? isPlanReportFetching
    : isTeamMemberXpFetching;

  const error = isPlanPrint ? planReportError : teamMemberXpError;

  // Silently log errors
  useEffect(() => {
    if (error) {
      console.warn('[PrintReport] Fetch error:', error);
      datadogRum.addError(error);
    }
  }, [error]);

  // --- Report Generation ---
  const generateReport = async (showDoB: boolean, locations: string[]) => {
    if (!subject) return;

    if (isPlanPrint) {
      const reportData = buildNonComplianceReport(locations);

      const tableHead = [
        [
          Constants.REPORT_TABLE.NAME,
          ...(showDoB ? [Constants.REPORT_TABLE.DATE_OF_BIRTH] : []),
          ...(isCompliance
            ? [
                Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN,
                Constants.REPORT_TABLE.COMPLETION_DATE,
                Constants.REPORT_TABLE.EXPIRATION_DATE,
              ]
            : [
                Constants.REPORT_TABLE.ITEMS_COMPLETED,
                Constants.REPORT_TABLE.TOTAL_TIME_SPENT_ON_PLAN,
                Constants.REPORT_TABLE.STATUS,
                Constants.REPORT_TABLE.COMPLETION_DATE,
              ]),
        ],
      ];

      const reportToGenerate = {
        category: t(mapCategoryToAliasTranslation(subject.plan.category ?? '')),
        foodSafetyImage: getFoodSafetyImageFromPlanReport(
          planReportAndAssignments,
          locations,
        ),
        id: subject.plan.id,
        location: `${t('Generic.at')} ${
          !locations.length || printLocations.length < 2
            ? printLocations.toString()
            : arrayToCommaString(locations, t('Generic.and'))
        }`,
        name: itemByLanguage(subject.plan.name) ?? '',
        // tableHead always has one row, but keep this defensive for typing/lint safety
        reportToPrint: reportData.map(teamMember =>
          (tableHead[0] ?? []).map(headerItem => teamMember[headerItem]),
        ),
        tableHead,
      };

      // Lazy-load pdf generator to reduce initial bundle size
      const pdf = await import('./pdfGenerator');

      pdf.createPdfAndNotifyNative(reportToGenerate, reportData, tableHead);
    } else if (isTeamMemberPrint) {
      const statusReportToPrint = [...xpPlans.map(mapXpStorePlanToReport)].sort(
        (planA, planB) => {
          const nameKey = Constants.REPORT_TABLE.PLAN;
          return (planA[nameKey] ?? '').localeCompare(planB[nameKey] ?? '');
        },
      );

      const { complianceReport, storeReport } =
        splitPrintReportRows(statusReportToPrint);

      const orderedRows = [...complianceReport, ...storeReport];

      const { footnotes, resolvePlanName } = shouldIncludePlanDescriptions
        ? buildPrintReportFootnotes(orderedRows)
        : emptyPrintReportFootnotesResult;

      const reportToGenerate = {
        category: t('Generic.teamMember'),
        isTeamMember: true,
        name: subject.name,
        complianceReport: buildComplianceTableBody(
          complianceReport,
          resolvePlanName,
        ),
        complianceTableHead: Constants.COMPLIANCE_TABLE_HEAD,
        printReportFootnotes: footnotes.map(footnote => ({
          marker: footnote.marker,
          description: footnote.description,
        })),
        reportToPrint: buildStoreTableBody(storeReport, resolvePlanName),
        tableHead: Constants.TABLE_HEAD,
        trainingProviderLine:
          footnotes.length > 0
            ? t('TrainingPlans.planDescription.printTrainingProvider')
            : undefined,
      };

      // Lazy-load pdf generator to reduce initial bundle size
      const pdf = await import('./pdfGenerator');

      pdf.createPdfAndNotifyNative(
        reportToGenerate,
        statusReportToPrint,
        Constants.TABLE_HEAD,
        true,
      );
    }
  };

  return {
    isFetching,
    generateReport,
    printLocations,
    startFetching,
  };
};
